<template>
	<div class="flex xs-12 md-7 xs-column">
		<div class="flex wrap mb2">
			<div class="flex xs-12 md-6 xs-left xs-middle">
				<h2 class="fs7 lh125 fw7 c1 mr4">Proposals</h2>
			</div>
			<div class="flex xs-12 md-6 xs-left xs-bottom md-right md-middle">
				<j-button @click="openModal">Add Proposal</j-button>
			</div>
		</div>
		<j-card class="mb4 bg5" v-for="proposal of proposals" :key="proposal.id">
			<div class="xs-12 flex wrap xs-middle p4 cu2">
				<div
					class="flex xs-7 xs-between wrap"
					@click="$emit('editProposal', proposal)"
				>
					<div class="xs-12 fs4 c6 fw7 lh150">
						<div class="flex mb2 xs-middle">
							{{ proposal.number }}
							<j-tag
								v-if="proposal.renewalCount"
								:label="$t('renewal')"
								variant="neutral"
								class="ml2"
							></j-tag>
						</div>
						<a @click="$emit('editProposal', proposal)">{{
							proposal.name || "Unnamed Proposal"
						}}</a>
						<span class="fs3 fw3 pl2"
							>({{ formatDate(proposal.createdAt) }})</span
						>
					</div>
					<div v-for="(offer, i) in proposal.offers" :key="i" class="mt1">
						<div class="xs-12 fw5 lh150">
							{{ `${offer.product.name} - ${offer.premium ? offer.premium.formattedPrice : ""}` }}
						</div>
					</div>
				</div>
				<v-popover
					class="xs-5 flex xs-right"
					placement="bottom-end"
					popoverClass="xs-12 r2"
					popoverArrowClass="xs-0"
					popoverInnerClass="xs-12 r2 bg5 b1 bc7"
				>
					<j-button
						type="bordered"
						size="xsmall"
						:isLoading="isActionLoading === proposal.id"
					>
						Actions
						<crm-icon name="down" color="c2" size="small" />
					</j-button>
					<template slot="popover">
						<div
							class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
							@click="createPolicies({ proposalId: proposal.id })"
							v-close-popover
						>
							Create Policy
						</div>
						<div
							class="xs-12 bb1 bc7 p3 fw7 cu2 c1"
							@click="renewProposal({ proposalId: proposal.id })"
							v-close-popover
						>
							Renew This Proposal
						</div>

						<div
							class="xs-12 p3 fw7 cError cu2"
							@click="removeProposal({ proposalId: proposal.id })"
							v-close-popover
						>
							Delete Proposal
						</div>
					</template>
				</v-popover>
			</div>
		</j-card>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import JTag from "@/components/JTag/"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

export default {
	data() {
		return {
			proposal: {},
			isActionLoading: false
		}
	},
	components: {
		JCard,
		JButton,
		CrmIcon,
		JTag
	},
	computed: {
		proposals() {
			return (this.$store?.state?.caseManagement?.proposals || []).map(item => {
				return {
					number: item?.proposalNumber,
					name: item?.name,
					createdAt: item?.createdAt,
					id: item?.id,
					offerId: item?.basket?.[0]?.id, // for single layer products
					product: item?.basket?.[0]?.product, // for single layer products
					renewedFromId: item?.renewalHistory?.renewedFrom?.id,
					renewalCount: item?.renewalHistory?.renewalCount,
					offers: item.basket,
					currencyCode: item?.totalPrice?.currencyCode
				}
			})
		}
	},
	methods: {
		formatDate(dateString) {
			return dayjs(dateString).format(this.$store.state.settings.dateTimeFormat)
		},
		openModal() {
			this.$emit("toggleAddProposalModal")
		},

		async removeProposal({ proposalId }) {
			const query = gql`
				mutation removeProposal($caseId: String!, $proposalId: String!) {
					removeProposal(caseId: $caseId, proposalId: $proposalId) {
						status
						errors
					}
				}
			`

			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId
				}
			})
			this.isActionLoading = ""

			// Error handling
			if (res?.data?.removeProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.removeProposal?.errors?.[0] || [this.$t("errors.general")]
				)
				return
			}

			if (this.$store.state.caseManagement.proposalId === proposalId) {
				this.$store.commit("caseManagement/SET_PROPOSAL_ID", null)
				this.$store.commit("caseManagement/SET_OFFER_ID", null)
			}

			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [] }
			})
		},

		async renewProposal({ proposalId }) {
			const query = gql`
				mutation renewProposal($caseId: String!, $input: renewProposalInput!) {
					renewProposal(caseId: $caseId, input: $input) {
						status
						errors
					}
				}
			`
			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input: {
						renewedFromId: proposalId
					}
				}
			})
			this.isActionLoading = ""

			// Check errors
			if (res?.data?.renewProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.renewProposal?.errors?.[0] || this.$t("errors.general")
				)
				return
			}

			// Succes
			// this.$emit("proposal:renewed")
			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [] }
			})
		},

		async createPolicies({ proposalId }) {
			const query = gql`
				mutation generatePoliciesFromProposal(
					$caseId: String!
					$proposalId: String!
				) {
					generatePoliciesFromProposal(
						caseId: $caseId
						proposalId: $proposalId
					) {
						status
						errors
					}
				}
			`

			this.isActionLoading = proposalId
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					proposalId
				}
			})
			this.isActionLoading = ""

			// Errors
			if (res?.data?.generatePoliciesFromProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.generatePoliciesFromProposal?.errors?.[0] ||
						this.$t("errors.general")
				)
				return
			}

			// Show success message
			this.$store.dispatch("addMessage", {
				delay: "1000",
				message: { type: "success", text: "Successfully created" }
			})

			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [] }
			})
		}
	}
}
</script>

<style></style>
